import React, { createContext, useContext, useReducer, useEffect } from "react";
import axios from "axios";

// Configure axios base URL
axios.defaults.baseURL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_SUCCESS":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
      };
    case "LOGOUT":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: action.payload,
      };
    case "AUTH_ERROR":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    isAuthenticated: false,
    user: null,
    token: localStorage.getItem("token"),
    loading: true,
    error: null,
  });

  // Set auth token in axios header
  useEffect(() => {
    if (state.token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${state.token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [state.token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const res = await axios.get("/api/auth/me");
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: {
              user: res.data.user,
              token,
            },
          });
        } catch (error) {
          dispatch({
            type: "AUTH_ERROR",
            payload: error.response?.data?.message,
          });
          localStorage.removeItem("token");
        }
      } else {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    loadUser();
  }, []);

  const login = async (email, password) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const res = await axios.post("/api/auth/login", { email, password });

      localStorage.setItem("token", res.data.token);
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: {
          user: res.data.user,
          token: res.data.token,
        },
      });

      return { success: true };
    } catch (error) {
      dispatch({ type: "AUTH_ERROR", payload: error.response?.data?.message });
      return { success: false, error: error.response?.data?.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const res = await axios.post("/api/auth/register", {
        name,
        email,
        password,
      });

      localStorage.setItem("token", res.data.token);
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: {
          user: res.data.user,
          token: res.data.token,
        },
      });

      return { success: true };
    } catch (error) {
      dispatch({ type: "AUTH_ERROR", payload: error.response?.data?.message });
      return { success: false, error: error.response?.data?.message };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    dispatch({ type: "LOGOUT" });
  };

  const value = {
    ...state,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
